// Explore — 浏览公开仓库 — Fluent UI v9
//
// 特性:
//   卡片占满页面宽度(单列)
//   服务端检索(keyword 防抖后打 /explore/repos?q=)
//   仓库名前缀补全(/explore/repos/suggest → Combobox 待选项)
//   服务端分页,每页 10 条
//   分页 sticky 固定底部,不随内容高度移动
import {
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  makeStyles,
  tokens,
  Button,
  Combobox,
  Option,
  Badge,
  Text,
  Spinner,
  MessageBar,
  MessageBarBody,
  Title3,
} from '@fluentui/react-components';
import {
  ArrowClockwiseRegular,
  SearchRegular,
  GlobeRegular,
  CompassNorthwestRegular,
  DismissRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
  DatabaseRegular,
} from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { reposApi } from '@/api/repos';
import type { PublicRepo } from '@/api/types';
import { explainError } from '@/api/client';
import { HelpPopover } from '@/components/HelpPopover';
import { SimpleList } from '@/components/SimpleList';
import { RepoDetailDialog } from '@/components/RepoDetailDialog';

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 350;

const useStyles = makeStyles({
  // 占满 contentBody 可视区高度;内部 body 滚动,分页固定贴底
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalXL,
    flexWrap: 'wrap',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    margin: 0,
    fontWeight: 600,
  },
  searchInput: {
    width: '320px',
    maxWidth: '100%',
  },
  searchWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  searchIcon: {
    display: 'flex',
    alignItems: 'center',
    color: tokens.colorNeutralForeground3,
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    paddingBottom: tokens.spacingVerticalL,
  },
  // 列表行内用图标/附加信息(与仓库管理 SimpleList 风格一致)
  repoIcon: {
    fontSize: '22px',
    color: tokens.colorBrandForeground1,
  },
  extraText: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  // 分页固定贴底:body flex:1 吸收全部剩余高度,分页紧跟其后贴在可视区底部
  pagination: {
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: '10px 0',
    backgroundColor: tokens.colorNeutralBackground1,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  pageInfo: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
  },
});

export function ExplorePage() {
  const styles = useStyles();
  const [draft, setDraft] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [pageIndex, setPageIndex] = useState(1);
  const [queryError, setQueryError] = useState<string | null>(null);
  // 仓库详情对话框:默认关闭,选中仓库时记录 owner/name
  const [detail, setDetail] = useState<{ owner: string; name: string } | null>(null);

  // 输入防抖 → 服务端检索关键词
  useEffect(() => {
    const t = setTimeout(() => {
      // 空串保留原样(匹配"清空关键字"),trim 仅用于判断
      setDebouncedQ(draft);
      setPageIndex(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [draft]);

  const offset = (pageIndex - 1) * PAGE_SIZE;

  // 服务端检索 + 分页
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['explore-repos', debouncedQ, pageIndex],
    queryFn: () =>
      reposApi.explorePublic({
        q: debouncedQ,
        limit: PAGE_SIZE,
        offset,
      }),
  });

  // 前缀补全待选项:输入非空前缀时触发
  const trimmedDraft = draft.trim();
  const { data: suggestions = [] } = useQuery({
    queryKey: ['explore-repos-suggest', trimmedDraft],
    queryFn: () => reposApi.suggestRepos({ q: trimmedDraft, limit: 10 }),
    enabled: trimmedDraft.length > 0,
  });

  // 错误提示
  useEffect(() => {
    if (error) {
      const msg = explainError(error);
      console.error(msg);
      setQueryError(msg);
    } else {
      setQueryError(null);
    }
  }, [error]);

  const repos: PublicRepo[] = data?.repos ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const suggestOptions = useMemo(
    () =>
      suggestions.map((r) => ({
        value: r.name,
        text: `${r.owner}/${r.name}`,
      })),
    [suggestions],
  );

  function openRepo(repo: PublicRepo) {
    setDetail({ owner: repo.owner, name: repo.name });
  }

  const hasQuery = debouncedQ.trim().length > 0;

  return (
    <div className={styles.root}>
      {/* 1. 顶部工具栏 */}
      <div className={styles.toolbar}>
        <Title3 as="h2" className={styles.title}>
          <CompassNorthwestRegular />
          浏览公开仓库
        </Title3>
        <div className={styles.searchWrap}>
          <div className={styles.searchIcon}>
            <SearchRegular />
          </div>
          <Combobox
            className={styles.searchInput}
            freeform
            value={draft}
            placeholder="搜索仓库名或描述"
            aria-label="搜索公开仓库"
            onOptionSelect={(_, d) => {
              // 选定待选项 → 立即作为检索关键词
              if (d.optionValue) {
                setDraft(d.optionText ?? d.optionValue);
              }
            }}
            onChange={(e) => setDraft(e.target.value)}
          >
            {trimmedDraft.length > 0 &&
              suggestOptions.map((o) => (
                <Option key={o.value} value={o.value} text={o.text}>
                  {o.text}
                </Option>
              ))}
          </Combobox>
          {draft ? (
            <Button
              appearance="transparent"
              size="small"
              icon={<DismissRegular />}
              aria-label="清除搜索"
              onClick={() => setDraft('')}
            />
          ) : undefined}
        </div>
        <HelpPopover content="按仓库名或描述模糊搜索公开仓库;输入仓库名前缀会弹出待选项;留空显示全部公开仓库。" />
        <Button
          appearance="outline"
          icon={isFetching ? <Spinner size="tiny" /> : <ArrowClockwiseRegular />}
          disabled={isFetching}
          onClick={() => refetch()}
        >
          刷新
        </Button>
      </div>

      {/* 查询错误条 */}
      {queryError && (
        <MessageBar intent="error" style={{ marginBottom: tokens.spacingVerticalL }}>
          <MessageBarBody>{queryError}</MessageBarBody>
        </MessageBar>
      )}

      {/* 2-3. 列表(SimpleList 风格:媒体图标+标题+描述),loading/空态由组件接管 */}
      <div className={styles.body}>
        <SimpleList
          loading={isLoading}
          emptyText={hasQuery ? '没有匹配的公开仓库' : '还没有公开仓库'}
          pageSize={Number.POSITIVE_INFINITY}
          items={repos.map((repo) => ({
            key: String(repo.id),
            media: <DatabaseRegular className={styles.repoIcon} />,
            title: (
              <>
                <span>
                  {repo.owner}/{repo.name}
                </span>
                <Badge appearance="filled" color="brand" icon={<GlobeRegular />}>
                  public
                </Badge>
              </>
            ),
            description: repo.description || '(无描述)',
            extra: (
              <span className={styles.extraText}>
                {new Date(repo.updated_at).toLocaleDateString('zh-CN')}
              </span>
            ),
            onClick: () => openRepo(repo),
          }))}
        />
      </div>

      {/* 4. 分页(0 条不显示,sticky 固定底部) */}
      {total > 0 && (
        <div className={styles.pagination}>
          <Button
            appearance="subtle"
            icon={<ChevronLeftRegular />}
            disabled={pageIndex <= 1}
            onClick={() => setPageIndex((p) => Math.max(1, p - 1))}
          />
          <Text className={styles.pageInfo}>
            第 {pageIndex} / {totalPages} 页 · 共 {total} 个
          </Text>
          <Button
            appearance="subtle"
            icon={<ChevronRightRegular />}
            disabled={pageIndex >= totalPages}
            onClick={() => setPageIndex((p) => Math.min(totalPages, p + 1))}
          />
        </div>
      )}

      {/* 仓库详情对话框 */}
      <RepoDetailDialog
        owner={detail?.owner ?? null}
        name={detail?.name ?? null}
        onClose={() => setDetail(null)}
      />
    </div>
  );
}